import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { ZoneApi, ZoneDataType } from "@/api/ZoneApi";
import { drain } from "./core/drain";
import { nextAvailableCode, normalizeZoneName } from "./resolveImportRows";

export type ExistingClientData = {
    clients: ClientDataType[];
    zones: ZoneDataType[];
};

/**
 * Trae todos los clientes y zonas del tenant.
 *
 * La importacion los necesita completos para detectar duplicados y para generar codigos
 * consecutivos sin volver a consultar por fila.
 */
export const loadExistingClientData = async (): Promise<ExistingClientData> => {
    const [clients, zones] = await Promise.all([drain(ClientApi), drain(ZoneApi)]);
    return { clients, zones };
};

/**
 * Crea las zonas que el archivo menciona y el tenant todavia no tiene.
 *
 * Se hace desde el navegador (son unas pocas, no cientos) para que `ZoneApi` invalide su
 * cache y para que la lambda reciba `zoneId` ya resueltos.
 */
export const createMissingZones = async (
    missingZoneNames: string[],
    existingZones: ZoneDataType[]
): Promise<ZoneDataType[]> => {
    if (!missingZoneNames.length) return existingZones;

    // El consecutivo se calcula una vez y se incrementa en memoria.
    let nextCode = nextAvailableCode(existingZones.map((zone) => ({ codigo: `${zone.codigo ?? ""}` })));
    const created: ZoneDataType[] = [];

    for (const nombre of missingZoneNames) {
        const zone = await ZoneApi.create({ nombre: nombre.trim(), codigo: nextCode });
        nextCode += 1;
        created.push(zone);
    }

    return [...existingZones, ...created];
};

/** Zonas indexadas por nombre normalizado, para resolver `zoneId` tras crearlas. */
export const indexZonesByName = (zones: ZoneDataType[]): Map<string, string> => {
    const index = new Map<string, string>();
    zones.forEach((zone) => {
        const key = normalizeZoneName(zone.nombre);
        if (key && zone.id && !index.has(key)) index.set(key, zone.id);
    });
    return index;
};
